export interface Vec2 {
	x: number;
	y: number;
}

export interface Ball {
	id: number;
	pos: Vec2;
	vel: Vec2;
	radius: number;
	mass: number;
	color: string;
	trail: Vec2[];
	glowColor: string;
}

export interface PhysicsConfig {
	gravity: number;
	restitution: number;
	friction: number;
	airResistance: number;
}

export const DEFAULT_CONFIG: PhysicsConfig = {
	gravity: 980,
	restitution: 0.8,
	friction: 0.99,
	airResistance: 0.999,
};

let nextId = 0;

export function createBall(
	x: number,
	y: number,
	vx: number = 0,
	vy: number = 0,
	radius: number = 20,
	color?: string,
): Ball {
	const colors = [
		{ fill: "#ff006e", glow: "#ff006e" },
		{ fill: "#00f5d4", glow: "#00f5d4" },
		{ fill: "#fee440", glow: "#fee440" },
		{ fill: "#8338ec", glow: "#8338ec" },
		{ fill: "#3a86ff", glow: "#3a86ff" },
		{ fill: "#ff5400", glow: "#ff5400" },
		{ fill: "#06d6a0", glow: "#06d6a0" },
	];
	const c = colors[nextId % colors.length];
	const ball: Ball = {
		id: nextId++,
		pos: { x, y },
		vel: { x: vx, y: vy },
		radius,
		mass: radius * radius * Math.PI,
		color: color || c.fill,
		glowColor: color ? color : c.glow,
		trail: [],
	};
	return ball;
}

export function step(
	balls: Ball[],
	config: PhysicsConfig,
	dt: number,
	width: number,
	height: number,
): void {
	const maxTrailLength = 30;

	for (const ball of balls) {
		// Store trail
		ball.trail.push({ x: ball.pos.x, y: ball.pos.y });
		if (ball.trail.length > maxTrailLength) {
			ball.trail.shift();
		}

		// Apply gravity
		ball.vel.y += config.gravity * dt;

		// Apply air resistance
		ball.vel.x *= config.airResistance;
		ball.vel.y *= config.airResistance;

		// Update position
		ball.pos.x += ball.vel.x * dt;
		ball.pos.y += ball.vel.y * dt;

		// Wall collisions
		// Floor
		if (ball.pos.y + ball.radius > height) {
			ball.pos.y = height - ball.radius;
			ball.vel.y = -ball.vel.y * config.restitution;
			ball.vel.x *= config.friction;
		}
		// Ceiling
		if (ball.pos.y - ball.radius < 0) {
			ball.pos.y = ball.radius;
			ball.vel.y = -ball.vel.y * config.restitution;
		}
		// Right wall
		if (ball.pos.x + ball.radius > width) {
			ball.pos.x = width - ball.radius;
			ball.vel.x = -ball.vel.x * config.restitution;
		}
		// Left wall
		if (ball.pos.x - ball.radius < 0) {
			ball.pos.x = ball.radius;
			ball.vel.x = -ball.vel.x * config.restitution;
		}
	}

	// Ball-to-ball collisions
	for (let i = 0; i < balls.length; i++) {
		for (let j = i + 1; j < balls.length; j++) {
			const a = balls[i];
			const b = balls[j];

			const dx = b.pos.x - a.pos.x;
			const dy = b.pos.y - a.pos.y;
			const dist = Math.sqrt(dx * dx + dy * dy);
			const minDist = a.radius + b.radius;

			if (dist < minDist && dist > 0) {
				// Normal vector
				const nx = dx / dist;
				const ny = dy / dist;

				// Relative velocity
				const dvx = a.vel.x - b.vel.x;
				const dvy = a.vel.y - b.vel.y;

				// Relative velocity along normal
				const dvn = dvx * nx + dvy * ny;

				// Don't resolve if balls are moving apart
				if (dvn > 0) {
					const totalMass = a.mass + b.mass;
					const impulse = (2 * dvn * config.restitution) / totalMass;

					a.vel.x -= impulse * b.mass * nx;
					a.vel.y -= impulse * b.mass * ny;
					b.vel.x += impulse * a.mass * nx;
					b.vel.y += impulse * a.mass * ny;

					// Separate overlapping balls
					const overlap = minDist - dist;
					const sepX = (overlap * nx) / 2;
					const sepY = (overlap * ny) / 2;
					a.pos.x -= sepX;
					a.pos.y -= sepY;
					b.pos.x += sepX;
					b.pos.y += sepY;
				}
			}
		}
	}
}

export function drawScene(
	ctx: CanvasRenderingContext2D,
	balls: Ball[],
	width: number,
	height: number,
	showTrails: boolean = true,
	showVectors: boolean = false,
	showEnergy: boolean = false,
	config?: PhysicsConfig,
): void {
	// Clear with dark background
	ctx.fillStyle = "#0a0a0f";
	ctx.fillRect(0, 0, width, height);

	// Draw grid lines
	ctx.strokeStyle = "rgba(255, 255, 255, 0.03)";
	ctx.lineWidth = 1;
	const gridSize = 40;
	for (let x = 0; x < width; x += gridSize) {
		ctx.beginPath();
		ctx.moveTo(x, 0);
		ctx.lineTo(x, height);
		ctx.stroke();
	}
	for (let y = 0; y < height; y += gridSize) {
		ctx.beginPath();
		ctx.moveTo(0, y);
		ctx.lineTo(width, y);
		ctx.stroke();
	}

	// Draw border glow
	const borderGrad = ctx.createLinearGradient(0, 0, width, 0);
	borderGrad.addColorStop(0, "rgba(138, 43, 226, 0.3)");
	borderGrad.addColorStop(0.5, "rgba(0, 245, 212, 0.3)");
	borderGrad.addColorStop(1, "rgba(255, 0, 110, 0.3)");
	ctx.strokeStyle = borderGrad;
	ctx.lineWidth = 2;
	ctx.strokeRect(1, 1, width - 2, height - 2);

	for (const ball of balls) {
		// Draw trail
		if (showTrails && ball.trail.length > 1) {
			for (let i = 1; i < ball.trail.length; i++) {
				const alpha = (i / ball.trail.length) * 0.5;
				const trailRadius = (ball.radius * i) / ball.trail.length * 0.4;
				ctx.beginPath();
				ctx.arc(ball.trail[i].x, ball.trail[i].y, trailRadius, 0, Math.PI * 2);
				ctx.fillStyle = ball.glowColor + Math.floor(alpha * 255).toString(16).padStart(2, "0");
				ctx.fill();
			}
		}

		// Draw ball glow
		const glowGrad = ctx.createRadialGradient(
			ball.pos.x,
			ball.pos.y,
			ball.radius * 0.5,
			ball.pos.x,
			ball.pos.y,
			ball.radius * 3,
		);
		glowGrad.addColorStop(0, ball.glowColor + "40");
		glowGrad.addColorStop(1, ball.glowColor + "00");
		ctx.beginPath();
		ctx.arc(ball.pos.x, ball.pos.y, ball.radius * 3, 0, Math.PI * 2);
		ctx.fillStyle = glowGrad;
		ctx.fill();

		// Draw ball
		const ballGrad = ctx.createRadialGradient(
			ball.pos.x - ball.radius * 0.3,
			ball.pos.y - ball.radius * 0.3,
			ball.radius * 0.1,
			ball.pos.x,
			ball.pos.y,
			ball.radius,
		);
		ballGrad.addColorStop(0, "#ffffff");
		ballGrad.addColorStop(0.3, ball.color);
		ballGrad.addColorStop(1, ball.color + "aa");
		ctx.beginPath();
		ctx.arc(ball.pos.x, ball.pos.y, ball.radius, 0, Math.PI * 2);
		ctx.fillStyle = ballGrad;
		ctx.fill();

		// Ball border glow
		ctx.strokeStyle = ball.glowColor;
		ctx.lineWidth = 2;
		ctx.shadowColor = ball.glowColor;
		ctx.shadowBlur = 15;
		ctx.stroke();
		ctx.shadowBlur = 0;

		// Velocity vectors
		if (showVectors) {
			const scale = 0.1;
			const endX = ball.pos.x + ball.vel.x * scale;
			const endY = ball.pos.y + ball.vel.y * scale;

			ctx.beginPath();
			ctx.moveTo(ball.pos.x, ball.pos.y);
			ctx.lineTo(endX, endY);
			ctx.strokeStyle = "#ffffff";
			ctx.lineWidth = 2;
			ctx.shadowColor = "#ffffff";
			ctx.shadowBlur = 5;
			ctx.stroke();
			ctx.shadowBlur = 0;

			// Arrowhead
			const angle = Math.atan2(endY - ball.pos.y, endX - ball.pos.x);
			ctx.beginPath();
			ctx.moveTo(endX, endY);
			ctx.lineTo(
				endX - 8 * Math.cos(angle - Math.PI / 6),
				endY - 8 * Math.sin(angle - Math.PI / 6),
			);
			ctx.moveTo(endX, endY);
			ctx.lineTo(
				endX - 8 * Math.cos(angle + Math.PI / 6),
				endY - 8 * Math.sin(angle + Math.PI / 6),
			);
			ctx.strokeStyle = "#ffffff";
			ctx.lineWidth = 2;
			ctx.stroke();
		}

		// Energy display
		if (showEnergy && config) {
			const speed = Math.sqrt(ball.vel.x * ball.vel.x + ball.vel.y * ball.vel.y);
			const ke = 0.5 * ball.mass * speed * speed / 1000000;
			const pe = ball.mass * config.gravity * (height - ball.pos.y) / 1000000;

			ctx.font = "10px monospace";
			ctx.fillStyle = "#00f5d4";
			ctx.textAlign = "center";
			ctx.fillText(`KE: ${ke.toFixed(1)}`, ball.pos.x, ball.pos.y - ball.radius - 18);
			ctx.fillStyle = "#ff006e";
			ctx.fillText(`PE: ${pe.toFixed(1)}`, ball.pos.x, ball.pos.y - ball.radius - 6);
		}
	}
}
